import { useState } from 'react';
import { KeyRound, User } from 'lucide-react';
import { SectionedPageLayout } from '@/components/layout/SectionedPageLayout';
import type { SectionedPageLayoutItem } from '@/components/layout/SectionedPageLayout';
import { ProviderConnectionsCard } from '@/components/settings/ProviderConnectionsCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSession } from '@/hooks/useSession';

type AccountSection = 'account' | 'providers';

const accountSections: readonly SectionedPageLayoutItem<AccountSection>[] = [
  { id: 'account', label: 'Account', icon: User, description: 'Identity and role' },
  { id: 'providers', label: 'Provider Connections', icon: KeyRound, description: 'Personal keys' },
] as const;

export function AccountPage(): React.ReactElement {
  const [activeSection, setActiveSection] = useState<AccountSection>('account');
  const { user } = useSession();

  return (
    <SectionedPageLayout
      title="Account"
      description="Personal identity and assistant provider connections."
      sidebarLabel="Settings"
      items={accountSections}
      activeId={activeSection}
      onActiveChange={setActiveSection}
      contentClassName="bg-background"
    >
      {activeSection === 'account' && (
        <div className="max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle>Identity</CardTitle>
              <p className="mt-0.5 text-[11.5px] text-text-tertiary">
                Your GitHub login is the source of truth for who you are in Metis.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={`${user.githubLogin} avatar`}
                    className="size-14 rounded-full border-2 border-border-bright"
                  />
                ) : (
                  <div
                    className="flex size-14 items-center justify-center rounded-full border-2 border-border-bright text-lg font-bold text-primary-foreground"
                    style={{
                      background: 'linear-gradient(135deg, var(--accent-bright), var(--flame))',
                    }}
                  >
                    {user?.githubLogin.slice(0, 2).toUpperCase() ?? 'ME'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate font-mono text-base font-semibold text-text-primary">
                      {user?.githubLogin ?? 'Unknown user'}
                    </h2>
                    {user &&
                      (user.role === 'admin' ? (
                        <span className="inline-flex items-center rounded-full border border-flame/25 bg-flame-bg-soft px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-flame-hi">
                          admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-border bg-surface-elevated px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-text-secondary">
                          {user.role}
                        </span>
                      ))}
                  </div>
                  <p className="mt-0.5 truncate font-mono text-[12px] text-text-secondary">
                    {user?.email ?? 'No email on file'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeSection === 'providers' && (
        <div className="max-w-4xl">
          <ProviderConnectionsCard />
        </div>
      )}
    </SectionedPageLayout>
  );
}
